import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import Landing from "@/pages/Landing";
import Grading from "@/pages/Grading";
import Report from "@/pages/Report";
import SampleReport from "@/pages/SampleReport";
import Admin from "@/pages/Admin";

/**
 * Small always-visible strip when the backend is running in
 * PREVIEW_ECHO_MODE. Signaled via /api/rescore/ping.preview_echo_mode.
 * Prevents QA from mistaking fixture output for a real grade.
 */
const EchoBanner = () => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/rescore/ping`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => setOn(Boolean(d?.preview_echo_mode)))
      .catch(() => setOn(false));
  }, []);
  if (!on) return null;
  return (
    <div
      className="fixed top-0 inset-x-0 z-50 text-center text-xs py-1.5 font-medium tracking-wide"
      style={{
        background: "#c58c1a",
        color: "#fff8ea",
        letterSpacing: "0.05em",
      }}
      data-testid="preview-echo-banner"
    >
      PREVIEW ECHO MODE · fixture data · no live APIs are being called
    </div>
  );
};

const Navbar = () => (
  <nav className="fixed top-0 inset-x-0 z-40" style={{ background: "rgba(250,245,235,0.85)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--border-default)" }}>
    <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-4">
      <Link to="/" className="flex items-center gap-3" data-testid="nav-home">
        <img
          src="/fieldguide-logo.png"
          alt="FieldGuide"
          className="h-7 md:h-8 w-auto"
          data-testid="nav-logo"
        />
        <span className="label-eyebrow hidden sm:inline">Local Authority Grader</span>
      </Link>
      <div className="flex items-center gap-6">
        <a
          href="#how-it-works"
          className="text-sm transition-colors hidden md:block"
          style={{ color: "var(--text-secondary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand-sage-deep)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
          data-testid="nav-how-it-works"
        >
          Methodology
        </a>
        <Link
          to="/"
          className="btn-ghost text-sm !py-2 !px-4"
          data-testid="nav-grade-my-hotel"
        >
          Grade a hotel
        </Link>
      </div>
    </div>
  </nav>
);

const Footer = () => (
  <footer className="mt-24 py-12 px-6 md:px-10" style={{ borderTop: "1px solid var(--border-default)" }}>
    <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between gap-6 text-sm" style={{ color: "var(--text-muted)" }}>
      <div className="max-w-2xl">
        <img
          src="/fieldguide-logo.png"
          alt="FieldGuide"
          className="h-7 w-auto mb-2"
          data-testid="footer-logo"
        />
        <div className="label-eyebrow mb-3">The Local Authority Platform for Hotels</div>
        <p>
          The FieldGuide Local Authority Score is a FieldGuide diagnostic and is not affiliated with,
          endorsed by, or verified by Google, OpenAI, Perplexity, or any hotel brand. Results are a
          representative point-in-time sample. Scores do not guarantee bookings or revenue.
        </p>
        <p className="mt-4 text-xs" style={{ color: "var(--text-muted)" }}>
          FieldGuide™, FieldGuide Local Authority Score™, and Local Authority Score™ are trademarks of
          FieldGuide. All rights reserved.
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <span className="label-eyebrow">Scoring Version 1.0.0</span>
        <span>Platforms tested: ChatGPT · Google AI Overviews · Perplexity</span>
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <BrowserRouter>
      <div className="min-h-screen">
        <EchoBanner />
        <Navbar />
        <div className="pt-20">
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/sample-report" element={<SampleReport />} />
            <Route path="/grading/:analysisId" element={<Grading />} />
            <Route path="/report/:analysisId" element={<Report />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </div>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
